import { PayFrequencyOptionLabel, PayFrequencyOptionValue, PayFrequencyValueToLabel } from 'handlers/yourTotalIncome';
import { InputSelectOption } from 'components/InputSelect/InputSelect';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';

export enum YourEmployerInputLabel {
  EmployerName = 'Employer name',
  HireDate = 'Start of employment',
  JobTitle = 'Your job title',
  AnnualIncome = 'Gross annual income',
  PayFrequency = 'Pay Frequency',
  AdditionalIncome = 'Additional income',
  IsLicensedProfessional = 'I am a licensed healthcare professional',
  HealthcareLicenseNumber = 'Healthcare license number',
  HealthcareLicenseState = 'Healthcare license state',
}

export const PAY_FREQUENCY_OPTIONS = [
  { label: PayFrequencyOptionLabel.Weekly, value: PayFrequencyOptionValue.Weekly },
  { label: PayFrequencyOptionLabel.BiWeekly, value: PayFrequencyOptionValue.BiWeekly },
  { label: PayFrequencyOptionLabel.SemiMonthly, value: PayFrequencyOptionValue.SemiMonthly },
  { label: PayFrequencyOptionLabel.Monthly, value: PayFrequencyOptionValue.Monthly },
] as InputSelectOption[];

export const ANNUAL_INCOME_MAX_LENGTH = 14;
export const MONETARY_PREFIX = '$';

export const LOW_ANNUAL_INCOME_THRESHOLD = 20_000;
export const LOW_ANNUAL_INCOME_WARNING = 'Are you sure this is your annual income?';

export const PAY_PERIODS_PER_YEAR = Object.freeze({
  [PayFrequencyOptionValue.Weekly]: 52,
  [PayFrequencyOptionValue.BiWeekly]: 26,
  [PayFrequencyOptionValue.SemiMonthly]: 24,
  [PayFrequencyOptionValue.Monthly]: 12,
});

export const parseIncomeAmount = (income?: string | number | null): number | null => {
  if (income === null || income === undefined || income === '') {
    return null;
  }

  const parsed = Number(`${income}`.replace(/[^0-9.-]+/g, ''));

  return Number.isFinite(parsed) ? parsed : null;
};

export const isLowAnnualIncome = (income?: string | number | null): boolean => {
  const parsed = parseIncomeAmount(income);

  return parsed !== null && parsed > 0 && parsed < LOW_ANNUAL_INCOME_THRESHOLD;
};

export const getPayPeriodsPerYear = (payFrequency?: string | null): number => {
  if (payFrequency && payFrequency in PAY_PERIODS_PER_YEAR) {
    return PAY_PERIODS_PER_YEAR[payFrequency as PayFrequencyOptionValue];
  }

  return PAY_PERIODS_PER_YEAR[PayFrequencyOptionValue.BiWeekly];
};

export const getPayFrequencyLabel = (payFrequency?: string | null): PayFrequencyOptionLabel => {
  if (payFrequency && payFrequency in PayFrequencyValueToLabel) {
    return PayFrequencyValueToLabel[payFrequency as PayFrequencyOptionValue];
  }

  return PayFrequencyOptionLabel.BiWeekly;
};

export const getAnnualizedIncome = (income: number, payFrequency?: string | null): number =>
  Math.round(income * getPayPeriodsPerYear(payFrequency));

export type LowAnnualIncomeConfirmation = {
  enteredIncome: number;
  annualizedIncome: number;
  payFrequencyLabel: PayFrequencyOptionLabel;
  enteredIncomeLabel: string;
  annualizedIncomeLabel: string;
};

export const getLowAnnualIncomeConfirmation = (
  income?: string | number | null,
  payFrequency?: string | null,
): LowAnnualIncomeConfirmation | null => {
  const enteredIncome = parseIncomeAmount(income);

  if (enteredIncome === null || !isLowAnnualIncome(enteredIncome)) {
    return null;
  }

  const annualizedIncome = getAnnualizedIncome(enteredIncome, payFrequency);

  return {
    enteredIncome,
    annualizedIncome,
    payFrequencyLabel: getPayFrequencyLabel(payFrequency),
    enteredIncomeLabel: formatMonetaryAmount(enteredIncome),
    annualizedIncomeLabel: formatMonetaryAmount(annualizedIncome),
  };
};
